import openai from './openaiClient';

/**
 * Enhanced Compliance Analysis Service using OpenAI GPT-5
 * Analyzes financial content for regulatory compliance violations
 */

/**
 * Analyzes content for compliance violations using OpenAI
 * @param {string} content - The content to analyze
 * @param {string} platform - The platform where content will be published
 * @param {string} contentType - Type of content (marketing, educational, etc.)
 * @returns {Promise<Object>} Compliance analysis results
 */
export async function analyzeContentCompliance(content, platform = 'social_media', contentType = 'marketing') {
  try {
    const systemPrompt = `You are an expert regulatory compliance analyst for financial institutions. Your role is to identify potential violations in financial marketing content according to FDIC, CFPB, SEC, and FINRA regulations.

Analyze the provided content and identify:
1. FDIC insurance misrepresentations
2. Truth in Lending Act violations
3. Truth in Savings regulation violations
4. Misleading rate guarantees
5. Missing required disclosures
6. Deceptive advertising practices
7. Social media compliance issues
8. Investment advice violations (if applicable)

For each violation found, provide:
- The specific regulation violated
- The exact problematic text
- A detailed explanation of why it's problematic
- The severity level (Critical, High, Medium, Low)
- Specific corrective suggestions with explanations

Calculate an overall compliance score (0-100) and risk level.`;

    const userPrompt = `Analyze this financial ${contentType} content for ${platform}:

Content: "${content}"

Provide a comprehensive compliance analysis identifying all potential violations and suggestions for improvement.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'compliance_analysis',
          schema: {
            type: 'object',
            properties: {
              overallRisk: { 
                type: 'string',
                enum: ['Low', 'Medium', 'High', 'Critical']
              },
              complianceScore: { 
                type: 'number',
                minimum: 0,
                maximum: 100
              },
              violationCounts: {
                type: 'object',
                properties: {
                  critical: { type: 'number' },
                  high: { type: 'number' },
                  medium: { type: 'number' },
                  low: { type: 'number' }
                },
                required: ['critical', 'high', 'medium', 'low']
              },
              violations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'string' },
                    rule: { type: 'string' },
                    severity: { 
                      type: 'string',
                      enum: ['Critical', 'High', 'Medium', 'Low']
                    },
                    regulation: { type: 'string' },
                    flaggedText: { type: 'string' },
                    description: { type: 'string' },
                    explanation: { type: 'string' },
                    citation: { type: 'string' },
                    suggestions: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          text: { type: 'string' },
                          reason: { type: 'string' }
                        },
                        required: ['text', 'reason']
                      }
                    }
                  },
                  required: ['id', 'rule', 'severity', 'regulation', 'flaggedText', 'description', 'explanation', 'citation', 'suggestions']
                }
              }
            },
            required: ['overallRisk', 'complianceScore', 'violationCounts', 'violations'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error in compliance analysis:', error);
    throw new Error(`Compliance analysis failed: ${error.message}`);
  }
}

/**
 * Generates regulatory-compliant content suggestions
 * @param {string} originalContent - The original problematic content
 * @param {Array} violations - Array of identified violations
 * @param {string} platform - Target platform
 * @returns {Promise<Object>} Improved content suggestions
 */
export async function generateCompliantContent(originalContent, violations, platform) {
  try {
    const systemPrompt = `You are a financial marketing compliance expert. Your task is to rewrite marketing content to be fully compliant with financial regulations while maintaining its marketing effectiveness.

Key compliance requirements:
- FDIC insurance limits must be accurate ($250,000 per depositor)
- No guaranteed return language for variable products
- Include required disclosures for rates and terms
- Avoid misleading urgency tactics
- Ensure balanced and fair presentation
- Follow social media compliance guidelines

Maintain the marketing appeal while ensuring full regulatory compliance.`;

    const violationContext = violations?.map(v => 
      `- ${v?.rule}: ${v?.flaggedText} (${v?.severity})`
    )?.join('\n');

    const userPrompt = `Original content: "${originalContent}"

Platform: ${platform}

Identified violations:
${violationContext}

Create 3 compliant versions of this content that:
1. Fix all identified violations
2. Maintain marketing effectiveness
3. Include necessary disclosures
4. Follow platform-specific best practices

For each version, explain what changes were made and why.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'compliant_content',
          schema: {
            type: 'object',
            properties: {
              suggestions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    version: { type: 'string' },
                    content: { type: 'string' },
                    changes: { type: 'string' },
                    complianceScore: { type: 'number' }
                  },
                  required: ['version', 'content', 'changes', 'complianceScore']
                }
              }
            },
            required: ['suggestions'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'medium'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating compliant content:', error);
    throw new Error(`Content generation failed: ${error.message}`);
  }
}

/**
 * Explains regulatory rules in context
 * @param {string} regulation - The regulation to explain
 * @param {string} context - The specific context or violation
 * @returns {Promise<Object>} Detailed regulatory explanation
 */
export async function explainRegulation(regulation, context = '') {
  try {
    const systemPrompt = `You are a regulatory compliance expert specializing in financial services regulations. Provide clear, comprehensive explanations of financial regulations that compliance officers can understand and apply.

Focus on:
- The purpose and scope of the regulation
- Key requirements and prohibitions
- Common violations and how to avoid them
- Practical implementation guidance
- Recent updates or interpretations`;

    const userPrompt = `Explain the regulation: "${regulation}"

${context ? `Context: ${context}` : ''}

Provide a detailed explanation including:
1. Overview and purpose
2. Key requirements
3. Common violations
4. Best practices for compliance
5. Penalties for non-compliance
6. Recent updates or guidance`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'regulation_explanation',
          schema: {
            type: 'object',
            properties: {
              regulation: { type: 'string' },
              overview: { type: 'string' },
              keyRequirements: {
                type: 'array',
                items: { type: 'string' }
              },
              commonViolations: {
                type: 'array',
                items: { type: 'string' }
              },
              bestPractices: {
                type: 'array',
                items: { type: 'string' }
              },
              penalties: { type: 'string' },
              recentUpdates: { type: 'string' }
            },
            required: ['regulation', 'overview', 'keyRequirements', 'commonViolations', 'bestPractices', 'penalties', 'recentUpdates'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error explaining regulation:', error);
    throw new Error(`Regulation explanation failed: ${error.message}`);
  }
}

/**
 * Analyzes similar content for compliance patterns
 * @param {string} currentContent - The current content being analyzed
 * @param {Array} historicalContent - Array of previously analyzed content
 * @returns {Promise<Object>} Comparative compliance analysis
 */
export async function analyzeSimilarContent(currentContent, historicalContent) {
  try {
    const systemPrompt = `You are a compliance pattern analyst. Compare the current content with historical content to identify compliance patterns, recurring issues, and provide contextual insights.

Analyze for:
- Similar compliance issues across content
- Patterns in violations
- Successful compliance approaches
- Risk trends
- Content effectiveness while maintaining compliance`;

    const historicalContext = historicalContent?.slice(0, 5)?.map((content, index) => 
      `${index + 1}. Content: "${content?.text || content?.preview}" (Compliance Score: ${content?.complianceScore || 'N/A'})`
    )?.join('\n');

    const userPrompt = `Current content: "${currentContent}"

Similar historical content:
${historicalContext}

Provide a comparative analysis including:
1. Similar compliance patterns
2. Recurring violation types
3. Success factors from high-scoring content
4. Risk assessment based on historical data
5. Recommendations for improvement`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'comparative_analysis',
          schema: {
            type: 'object',
            properties: {
              similarityScore: { type: 'number' },
              commonPatterns: {
                type: 'array',
                items: { type: 'string' }
              },
              riskTrends: {
                type: 'array',
                items: { type: 'string' }
              },
              successFactors: {
                type: 'array',
                items: { type: 'string' }
              },
              recommendations: {
                type: 'array',
                items: { type: 'string' }
              },
              riskAssessment: { type: 'string' }
            },
            required: ['similarityScore', 'commonPatterns', 'riskTrends', 'successFactors', 'recommendations', 'riskAssessment'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error analyzing similar content:', error);
    throw new Error(`Similar content analysis failed: ${error.message}`);
  }
}